import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { useAppDispatch } from '../../../app/store';
import { useGetGridMetadataQuery } from '../../menu';
import {
  gridApi,
  useGetGridDataQuery,
  useSaveBatchMutation,
  useDeleteBatchMutation,
} from '../api/gridApi';
import {
  parseColumnMetadata,
  convertLookupMapsToMap,
  extractFieldKeys,
  isSortSelectorAllowedForColumns,
  toSortDescriptors,
  toGridFilterFromColumnFilters,
  toGlobalSearchFilter,
  combineAndFilter,
  parseValidationRules,
  validateField,
  calculateDateRange,
  toGridFilterStartOfDay,
  toGridFilterEndOfDay,
  getGridDateTimeMode,
  formatFieldValueForSave,
  resolveGridParentId,
} from '../utils';
import sessionStorageService from '../../../services/sessionStorageService';
import { useGridData } from './useGridData';
import { useRowOperations } from './useRowOperations';
import { useNavigationGuard } from './useNavigationGuard';
import { useGridStatePersistence } from './useGridStatePersistence';
import { useConfigurationTables } from './useConfigurationTables';
import { useConfiguredGridVisibilityFilters } from './useConfiguredGridVisibilityFilters';
import { GridType, type Command } from '../../menu/types';
import { normalizeMediaBeforeSave } from '../../detail-form/richTextMediaHelpers';
import type {
  Entity,
  EntityField,
  GridFilter,
  SortDescriptor,
  ColumnMetadata,
  PersistedGridState,
  LookupDataItem,
} from '../types';
import type {
  ColumnFiltersState,
  ColumnSizingState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';

const DEFAULT_PAGE_SIZE = 25;

function areSortDescriptorsEqual(a: SortDescriptor[], b: SortDescriptor[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;

  return a.every((x, i) => x.selector === b[i].selector && x.desc === b[i].desc);
}

function isColumnEditable(column: ColumnMetadata): boolean {
  return column.canEdit !== false && column.editable !== false && column.allowEditing !== false;
}

function resolveInitialDateRange(command: Command): { startDate: string; endDate: string } {
  const range = command.defaultRange ?? 'Current month';
  const { startDate: start, endDate: end } = calculateDateRange(range);
  const baseDate = dayjs(start);
  const dateTimeMode = getGridDateTimeMode(command.settings);

  if (command.dateFilter === 'Month') {
    return {
      startDate: toGridFilterStartOfDay(baseDate.startOf('month'), dateTimeMode),
      endDate: toGridFilterEndOfDay(baseDate.endOf('month'), dateTimeMode),
    };
  }

  return {
    startDate: toGridFilterStartOfDay(baseDate, dateTimeMode),
    endDate: toGridFilterEndOfDay(dayjs(end), dateTimeMode),
  };
}

type UseGridPageLogicProps = {
  // Metadata source - either menuItemId OR nodeKey
  menuItemId?: number;
  nodeKey?: string;

  // Optional filters
  parentId?: string;
  detailFormKey?: string; // For constructing ParentId when saving in detail forms
  parentGridKey?: string; // Preferred parent fields container for child-grid saves
  tabsKey?: string;

  // Feature flags
  enableBatchOperations?: boolean; // If false, save/delete are no-ops
  enableNavigationGuard?: boolean;
  preferCachedOnMount?: boolean;

  // Skip conditions
  skip?: boolean;

  // Initial date range (e.g. from URL params)
  initialDateRange?: { startDate?: string; endDate?: string };
};

/** Master hook — orchestrates metadata, data fetching, filtering, batch save/delete and navigation guard */
export const useGridPageLogic = ({
  menuItemId,
  nodeKey,
  parentId,
  detailFormKey,
  parentGridKey,
  tabsKey,
  enableBatchOperations = true,
  enableNavigationGuard = true,
  preferCachedOnMount = false,
  skip = false,
  initialDateRange,
}: UseGridPageLogicProps) => {
  const dispatch = useAppDispatch();

  // Fetch grid metadata
  const {
    data: metadata,
    isLoading: metadataLoading,
    error: metadataError,
  } = useGetGridMetadataQuery(
    menuItemId
      ? { menuItemId, ...(parentId ? { parentId } : {}) }
      : { nodeKey: nodeKey!, ...(parentId ? { parentId } : {}) },
    {
      skip: skip || (!menuItemId && !nodeKey),
    }
  );

  // Parse columns
  const parsedColumns = useMemo(() => parseColumnMetadata(metadata?.columns), [metadata?.columns]);
  const configurationTables = useConfigurationTables(metadata?.configurationTables);

  const fieldKeys = useMemo(
    () => extractFieldKeys(metadata?.columns as ColumnMetadata[] | undefined),
    [metadata?.columns]
  );

  // undefined = metadata not yet loaded; null = loaded but no zgridfilter command; Command = loaded with command
  const dateFilterCommand = useMemo<Command | null | undefined>(() => {
    if (!metadata) return undefined;
    return metadata.commands?.find((c) => c.alias === 'zgridfilter') ?? null;
  }, [metadata]);

  // Grid state persistence (save/restore column visibility, sorting, sizing, page size)
  const {
    savedState: persistedState,
    isLoading: persistedStateLoading,
    isFetching: persistedStateFetching,
    saveState: persistState,
    clearState: clearPersistedState,
  } = useGridStatePersistence(metadata?.contentId);

  // Track whether we've already applied the persisted state so we do it only once
  const appliedPersistedStateRef = useRef(false);
  // Tracks per-navigation whether the perf mark for query-unblocked has been emitted
  const _queryUnblockedRef = useRef(false);

  // Convert persisted sorting (SortDescriptor[]) to TanStack SortingState — drop non-sortable columns (Umbraco config).
  const persistedSortingForTable = useMemo<SortingState>(() => {
    if (!persistedState?.sorting?.length) return [];
    return persistedState.sorting
      .filter((s) => isSortSelectorAllowedForColumns(s.selector, parsedColumns))
      .map((s) => ({ id: s.selector, desc: s.desc }));
  }, [persistedState?.sorting, parsedColumns]);

  const sessionSortKey = useMemo(() => {
    if (!metadata?.contentKey) return null;
    return `GRID_SORTING:${metadata.contentKey}:${parentId ?? ''}`;
  }, [metadata?.contentKey, parentId]);

  const sessionDateRangeKey = useMemo(
    () =>
      metadata?.contentKey ? `GRID_DATE_RANGE:${metadata.contentKey}:${parentId ?? ''}` : null,
    [metadata?.contentKey, parentId]
  );
  const sessionColumnFiltersKey = useMemo(
    () =>
      metadata?.contentKey ? `GRID_COLUMN_FILTERS:${metadata.contentKey}:${parentId ?? ''}` : null,
    [metadata?.contentKey, parentId]
  );

  const sessionColumnFilters = useMemo<ColumnFiltersState>(() => {
    if (!sessionColumnFiltersKey) return [];
    try {
      const stored = sessionStorageService.get<ColumnFiltersState>(sessionColumnFiltersKey);
      return Array.isArray(stored)
        ? stored.filter(
            (filter) =>
              !!filter &&
              typeof filter.id === 'string' &&
              filter.value !== undefined &&
              parsedColumns.some((column) => (column.field || column.dataField) === filter.id)
          )
        : [];
    } catch {
      return [];
    }
  }, [sessionColumnFiltersKey, parsedColumns]);

  const initialColumnFiltersForTable = sessionColumnFilters;

  const sessionSortDescriptors = useMemo<SortDescriptor[]>(() => {
    if (!sessionSortKey) return [];
    try {
      const parsed = sessionStorageService.get<SortDescriptor[]>(sessionSortKey);
      if (!parsed || !Array.isArray(parsed)) return [];
      return parsed.filter(
        (s) =>
          !!s &&
          typeof s.selector === 'string' &&
          typeof s.desc === 'boolean' &&
          isSortSelectorAllowedForColumns(s.selector, parsedColumns)
      );
    } catch {
      return [];
    }
  }, [sessionSortKey, parsedColumns]);

  const sessionSortingForTable = useMemo<SortingState>(() => {
    if (!sessionSortDescriptors.length) return [];
    return sessionSortDescriptors.map((s) => ({ id: s.selector, desc: s.desc }));
  }, [sessionSortDescriptors]);

  const defaultSortDescriptor = useMemo<SortDescriptor[]>(() => {
    const selector = metadata?.defaultSortingColumn;
    if (!selector) return [];
    // Validate against current columns so we don't apply stale/invalid defaults.
    if (!isSortSelectorAllowedForColumns(selector, parsedColumns)) return [];
    const desc = String(metadata?.defaultSortingOrder ?? '')
      .toLowerCase()
      .startsWith('desc');
    return [{ selector, desc }];
  }, [metadata?.defaultSortingColumn, metadata?.defaultSortingOrder, parsedColumns]);

  const defaultSortingForTable = useMemo<SortingState>(() => {
    if (!defaultSortDescriptor.length) return [];
    return defaultSortDescriptor.map((s) => ({ id: s.selector, desc: s.desc }));
  }, [defaultSortDescriptor]);

  /** Initial sorting for the table: session wins; then persisted state; then Umbraco default sort. */
  const initialSortingForTable = useMemo<SortingState>(() => {
    if (sessionSortingForTable.length > 0) return sessionSortingForTable;
    if (persistedSortingForTable.length > 0) return persistedSortingForTable;
    return defaultSortingForTable;
  }, [sessionSortingForTable, persistedSortingForTable, defaultSortingForTable]);

  // Grid filtering (pagination, sorting, filtering) - inline implementation
  const sessionPageKey = metadata?.contentKey
    ? `GRID_PAGE:${metadata.contentKey}:${parentId ?? ''}`
    : null;

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageIndex, setPageIndex] = useState(0);
  const [dateRange, setDateRange] = useState<{ startDate?: string; endDate?: string }>(
    initialDateRange ?? {} // seed from props if provided
  );
  const [filterResetTrigger, setFilterResetTrigger] = useState(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [serverSort, setServerSort] = useState<SortDescriptor[]>([]);
  const [serverFilter, setServerFilter] = useState<GridFilter | undefined>(undefined);
  const restoredFiltersActiveRef = useRef(true);

  // Show deleted toggle — persisted per grid in localStorage
  const showDeletedKey = metadata?.contentKey ? `TO_SHOW_DELETED_${metadata.contentKey}` : null;
  const [showDeleted, setShowDeleted] = useState<boolean>(() => {
    if (!showDeletedKey) return false;
    try {
      return localStorage.getItem(showDeletedKey) === 'true';
    } catch {
      return false;
    }
  });

  // Re-read localStorage when contentKey changes (metadata loads)
  useEffect(() => {
    if (!showDeletedKey) return;
    try {
      setShowDeleted(localStorage.getItem(showDeletedKey) === 'true');
    } catch {
      setShowDeleted(false);
    }
  }, [showDeletedKey]);

  const handleShowDeletedToggle = useCallback(() => {
    setShowDeleted((prev) => {
      const next = !prev;
      if (showDeletedKey) {
        try {
          localStorage.setItem(showDeletedKey, String(next));
        } catch {
          /* ignore */
        }
      }
      return next;
    });
  }, [showDeletedKey]);

  // Reset all transient state when switching between grids.
  // Also resets appliedPersistedStateRef so the next effect can apply the new grid's
  // persisted state once it loads asynchronously.
  const gridIdentity = `${menuItemId ?? ''}-${nodeKey ?? ''}-${parentId ?? ''}`;
  const visibilityFilters = useConfiguredGridVisibilityFilters(metadata?.commands, gridIdentity);

  useEffect(() => {
    if (!sessionPageKey) return;
    try {
      const saved = sessionStorageService.get<number>(sessionPageKey);
      if (saved != null && saved > 0) {
        setPageIndex(saved);
      }
    } catch {
      /* ignore */
    }
  }, [sessionPageKey, gridIdentity]);

  useEffect(() => {
    setSearchTerm('');
    setPageSize(DEFAULT_PAGE_SIZE);
    setPageIndex(0);
    setDateRange({});
    setServerSort([]);
    setServerFilter(undefined);
    restoredFiltersActiveRef.current = true;
    appliedPersistedStateRef.current = false;
    _queryUnblockedRef.current = false;
  }, [gridIdentity]);

  // Server-side initial sort: ensure the FIRST request already includes Umbraco default sorting
  // unless the user has explicit persisted/session sorting.
  const effectiveServerSort = useMemo<SortDescriptor[]>(() => {
    if (serverSort.length > 0) return serverSort;
    if (sessionSortDescriptors.length > 0) return sessionSortDescriptors;
    if (persistedState?.sorting?.length) return persistedState.sorting;
    return defaultSortDescriptor;
  }, [serverSort, sessionSortDescriptors, persistedState?.sorting, defaultSortDescriptor]);

  // Initialize dateRange for grids that have a date filter command.
  // No-filter grids (dateFilterCommand === null) need no initialization here;
  // dateRangeInitialized (memo below) becomes true as soon as metadata loads.
  useEffect(() => {
    if (!dateFilterCommand) return;
    if (initialDateRange?.startDate && initialDateRange?.endDate) {
      setDateRange({
        startDate: initialDateRange.startDate,
        endDate: initialDateRange.endDate,
      });
      return;
    }

    if (sessionDateRangeKey) {
      try {
        const stored = sessionStorageService.get<{ startDate?: string; endDate?: string }>(
          sessionDateRangeKey
        );
        if (stored?.startDate && stored.endDate) {
          setDateRange(stored);
          return;
        }
      } catch {
        /* ignore malformed session state */
      }
    }

    setDateRange(resolveInitialDateRange(dateFilterCommand));
  }, [
    dateFilterCommand,
    gridIdentity,
    initialDateRange?.startDate,
    initialDateRange?.endDate,
    sessionDateRangeKey,
  ]);

  // dateRangeInitialized is derived synchronously from metadata:
  // - undefined  → metadata not yet loaded → block query
  // - null       → no date filter → unblock immediately (no extra effect cycle)
  // - Command    → has date filter → unblock once dateRange has values (set by effect above)
  const dateRangeInitialized = useMemo(() => {
    if (dateFilterCommand === undefined) return false;
    if (dateFilterCommand === null) return true;
    return !!(dateRange.startDate && dateRange.endDate);
  }, [dateFilterCommand, dateRange.startDate, dateRange.endDate]);

  // ─── Page size initialisation ─────────────────────────────────────────────
  // Step 1: as soon as metadata arrives apply usePaging / numberOfRowsPerPage.
  //         This runs before persisted state loads so the first data fetch and
  //         TanStack pagination already use the correct size.
  useEffect(() => {
    if (!metadata) return;

    if (metadata.numberOfRowsPerPage && metadata.numberOfRowsPerPage > 0) {
      setPageSize(metadata.numberOfRowsPerPage);
    }
    // If neither is set, keep DEFAULT_PAGE_SIZE (25) until persisted state loads.
    // gridIdentity resets pageSize to DEFAULT first, then this re-applies metadata value.
  }, [metadata, gridIdentity]);

  // Step 2: once persisted state loads, let the user's saved page size win
  //         (only applied once per grid – tracked by appliedPersistedStateRef).
  useEffect(() => {
    if (appliedPersistedStateRef.current || persistedStateLoading || !persistedState) return;
    appliedPersistedStateRef.current = true;

    if (persistedState.pageSize) {
      setPageSize(persistedState.pageSize);
    }
  }, [persistedState, persistedStateLoading]);

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setPageSize(newPageSize);
      setPageIndex(0);
      if (!persistedStateLoading) {
        persistState({
          ...(persistedState ?? {}),
          pageSize: newPageSize,
        });
      }
    },
    [persistedState, persistedStateLoading, persistState]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPageIndex(newPage);
      if (sessionPageKey) {
        try {
          if (newPage > 0) {
            sessionStorageService.save(sessionPageKey, newPage);
          } else {
            sessionStorageService.remove(sessionPageKey);
          }
        } catch {
          /* ignore */
        }
      }
    },
    [sessionPageKey]
  );

  const resetAllFilters = useCallback(() => {
    setSearchTerm('');
    setPageIndex(0);
    setServerSort([]);
    setServerFilter(undefined);
    restoredFiltersActiveRef.current = false;
    if (sessionDateRangeKey) sessionStorageService.remove(sessionDateRangeKey);
    if (sessionColumnFiltersKey) sessionStorageService.remove(sessionColumnFiltersKey);
    if (dateFilterCommand) setDateRange(resolveInitialDateRange(dateFilterCommand));
    if (sessionSortKey) {
      try {
        sessionStorageService.remove(sessionSortKey);
      } catch {
        /* ignore */
      }
    }
    setFilterResetTrigger((prev) => prev + 1); // Trigger filter row reset in DataGrid
  }, [dateFilterCommand, sessionColumnFiltersKey, sessionDateRangeKey, sessionSortKey]);

  const handleDateRangeChange = useCallback(
    (startDate: string, endDate: string) => {
      setDateRange({ startDate, endDate });
      if (sessionDateRangeKey) {
        sessionStorageService.save(sessionDateRangeKey, { startDate, endDate });
      }
    },
    [sessionDateRangeKey]
  );

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
    setPageIndex(0);
  }, []);

  const handleSortChange = useCallback(
    (sortingState: SortingState) => {
      const next = toSortDescriptors(sortingState, parsedColumns);
      setServerSort(next);
      if (sessionSortKey) {
        try {
          if (next.length === 0) {
            sessionStorageService.remove(sessionSortKey);
            setPageIndex(0);
            return;
          }

          // Don't persist default sorting on initial hydration — only persist user-driven changes.
          const existing = sessionStorageService.get<SortDescriptor[]>(sessionSortKey);
          const isInitialHydrationWrite =
            (existing == null || existing.length === 0) &&
            !persistedState?.sorting?.length &&
            areSortDescriptorsEqual(next, defaultSortDescriptor);

          if (!isInitialHydrationWrite) {
            sessionStorageService.save(sessionSortKey, next);
          }
        } catch {
          /* ignore */
        }
      }
      setPageIndex(0);
    },
    [parsedColumns, sessionSortKey, persistedState?.sorting, defaultSortDescriptor]
  );

  const handleFilterChange = useCallback(
    (columnFilters: ColumnFiltersState) => {
      restoredFiltersActiveRef.current = false;
      setServerFilter(toGridFilterFromColumnFilters(columnFilters, parsedColumns));
      if (sessionColumnFiltersKey) {
        if (columnFilters.length > 0) {
          sessionStorageService.save(sessionColumnFiltersKey, columnFilters);
        } else {
          sessionStorageService.remove(sessionColumnFiltersKey);
        }
      }
      setPageIndex(0);
    },
    [parsedColumns, sessionColumnFiltersKey]
  );

  const combinedServerFilter = useMemo(() => {
    const userFilter = combineAndFilter(
      serverFilter ??
        (restoredFiltersActiveRef.current
          ? toGridFilterFromColumnFilters(sessionColumnFilters, parsedColumns)
          : undefined),
      toGlobalSearchFilter(searchTerm, parsedColumns)
    );
    return visibilityFilters.apply(userFilter);
  }, [serverFilter, sessionColumnFilters, searchTerm, parsedColumns, visibilityFilters]);

  const toggleGridVisibilityFilter = useCallback(
    (command: Command) => {
      visibilityFilters.toggle(command);
      setPageIndex(0);
    },
    [visibilityFilters]
  );

  // Determine the date filter type from grid metadata
  const dateFilterType = dateFilterCommand?.dateFilter;

  // Perf mark: track when the data query first becomes unblocked
  if (!_queryUnblockedRef.current && !!metadata?.contentKey && !skip && dateRangeInitialized) {
    _queryUnblockedRef.current = true;
    try {
      performance.mark('grid:query-unblocked');
      performance.measure('grid:tab-to-query', 'grid:tab-click', 'grid:query-unblocked');
    } catch {
      /* ignore */
    }
  }

  // Fetch grid data
  const {
    data: gridResult,
    isLoading: dataLoading,
    isFetching: dataFetching,
    error: dataError,
    refetch: refetchGridData,
  } = useGetGridDataQuery(
    {
      options: {
        nodeKey: metadata?.contentKey || '',
        fieldKeys,
        parentId: parentId ?? '',
        ...(metadata?.usePaging === true && { skip: pageIndex * pageSize, take: pageSize }),
        ...(dateRange.startDate && { startDate: dateRange.startDate }),
        ...(dateRange.endDate && { endDate: dateRange.endDate }),
        ...(showDeleted && { toShowDeleted: true }),
        ...(metadata?.usePaging === true &&
          effectiveServerSort.length > 0 && { sort: effectiveServerSort }),
        ...(metadata?.usePaging === true &&
          combinedServerFilter && { filter: combinedServerFilter }),
        // Date-filtered grids: send rowKeys with the start date (MM/DD/YYYY).
        // This matches legacy behaviour and routes the backend to the CollectionFromSql-only
        // code path, which avoids a second MapEntityList call that fails on type mismatches.
        ...(dateFilterType &&
          dateRange.startDate && {
            rowKeys: `${dayjs(dateRange.startDate).format('MM/DD/YYYY')}&endDate`,
          }),
      },
      columnMetadata: metadata?.columns as ColumnMetadata[] | undefined,
    },
    {
      skip: !metadata?.contentKey || skip || !dateRangeInitialized,
      refetchOnMountOrArgChange: preferCachedOnMount ? false : 15,
    }
  );

  const gridData = useMemo(() => gridResult?.data || [], [gridResult?.data]);
  const totalCount = gridResult?.totalCount || 0;
  const serverSummary = gridResult?.summary ?? null;

  const lookupMapsAsMap = useMemo(
    () => convertLookupMapsToMap(gridResult?.lookupMaps),
    [gridResult?.lookupMaps]
  );
  const rawLookupItemsAsMap = useMemo<Map<string, LookupDataItem[]>>(() => {
    const raw = gridResult?.rawLookupItems;
    if (!raw) return new Map();
    return new Map(Object.entries(raw));
  }, [gridResult?.rawLookupItems]);
  const filterLookupMapsAsMap = useMemo(
    () => convertLookupMapsToMap(gridResult?.filterLookupMaps),
    [gridResult?.filterLookupMaps]
  );

  // Build dropdown base filter from metadata filters (zDropdownRowFilter)
  const dropdownBaseFilter = useMemo(() => {
    if (!metadata?.filters || metadata.filters.length === 0) {
      return undefined;
    }

    // Filter for Kind === 'zDropdownRowFilter'
    const rowFilters = metadata.filters.filter((f) => f.Kind === 'zDropdownRowFilter');
    if (rowFilters.length === 0) {
      return undefined;
    }

    // Group filters by collection
    const collectionGroups = new Map<string, typeof rowFilters>();
    const nonCollectionFilters: typeof rowFilters = [];

    rowFilters.forEach((filter) => {
      const collection = filter.Options?.Collection;
      if (collection) {
        if (!collectionGroups.has(collection)) {
          collectionGroups.set(collection, []);
        }
        collectionGroups.get(collection)!.push(filter);
      } else {
        nonCollectionFilters.push(filter);
      }
    });

    const parts: unknown[] = [];

    // Process non-collection filters
    nonCollectionFilters.forEach((filter) => {
      const operator = filter.Operation;
      const value = filter.Value;
      let condition: unknown = [filter.Field, operator, value];

      // Apply NOT wrapper if needed
      if (filter.Options?.Not) {
        condition = ['!', condition];
      }

      if (parts.length > 0) {
        // Use OR if this filter has Or flag, otherwise AND
        parts.push(filter.Options?.Or ? 'or' : 'and');
      }
      parts.push(condition);
    });

    // Process collection groups
    collectionGroups.forEach((groupFilters) => {
      const groupParts: unknown[] = [];
      let hasOr = false;

      groupFilters.forEach((filter) => {
        const operator = filter.Operation;
        const value = filter.Value;
        let condition: unknown = [filter.Field, operator, value];

        if (filter.Options?.Not) {
          condition = ['!', condition];
        }

        if (groupParts.length > 0) {
          groupParts.push('and');
        }
        groupParts.push(condition);

        if (filter.Options?.Or) hasOr = true;
      });

      // Build group filter
      const groupFilter: unknown = groupParts.length === 1 ? groupParts[0] : groupParts;

      if (parts.length > 0) {
        // Use OR if any filter in group has Or flag, otherwise AND
        parts.push(hasOr ? 'or' : 'and');
      }
      parts.push(groupFilter);
    });

    // Return undefined if no parts, single part if only one, otherwise full array
    if (parts.length === 0) return undefined;
    if (parts.length === 1) return parts[0];
    return parts;
  }, [metadata?.filters]);

  // Grid data state management
  const gridDataHook = useGridData({
    gridData,
  });

  // Determine if grid supports inline editing.
  // Priority: GridType.Inline > no detail form configured > edit command isInlineEdit flag.
  // The last case handles grids where gridType is null and detailFormId is present,
  // but the edit command explicitly signals inline editing (e.g. TtTaskLink grid).
  const editCommandForInline = metadata?.commands?.find((cmd) => cmd.alias === 'edit');
  const inlineEditable =
    metadata?.gridType === GridType.Inline ||
    (metadata?.detailFormId == null && metadata?.detailFormKey == null) ||
    editCommandForInline?.isInlineEdit === true;

  // Row operations
  const rowOps = useRowOperations({
    gridData,
    parsedColumns,
    metadata,
    inlineEditable,
    setDisplayData: gridDataHook.setDisplayData,
    setModifiedRows: gridDataHook.setModifiedRows,
    setModifiedCells: gridDataHook.setModifiedCells,
    setMarkedForDeletion: gridDataHook.setMarkedForDeletion,
    setValidationErrors: gridDataHook.setValidationErrors,
    tempIdCounterRef: gridDataHook.tempIdCounterRef,
  });

  /** Inline delete: unsaved rows (temp_*) are removed immediately; persisted rows are marked for batch delete. */
  const handleInlineRowDelete = useCallback(
    (row: Record<string, unknown>) => {
      const rowId = String(row.Id ?? '');
      if (!rowId || rowId === '0') return;

      if (rowId.startsWith('temp_')) {
        rowOps.handleRollbackRow(rowId);
        return;
      }
      gridDataHook.setMarkedForDeletion((prev) => {
        const next = new Set(prev);
        if (next.has(rowId)) {
          next.delete(rowId);
        } else {
          next.add(rowId);
        }
        return next;
      });
    },
    // Intentionally omitted deps to preserve stable callback
    // for unsaved row deletion behavior in grid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowOps.handleRollbackRow, gridDataHook.setMarkedForDeletion]
  );

  // Batch operations (if enabled)
  const [saveBatch] = useSaveBatchMutation();
  const [deleteBatch] = useDeleteBatchMutation();
  const [isBatchSaving, setIsBatchSaving] = useState(false);

  const handleBatchSave = useCallback(async () => {
    if (!metadata?.contentKey || !enableBatchOperations) return false;

    // Pre-save validation
    const errors = new Map<string, string>();

    gridDataHook.modifiedRows.forEach((modifiedRow) => {
      // Skip rows marked for deletion
      if (gridDataHook.markedForDeletion.has(modifiedRow.rowId)) return;

      parsedColumns.forEach((col) => {
        const fieldName = col.field || col.dataField;
        if (!fieldName || !col.validationRules) return;
        if (!isColumnEditable(col)) return;

        const rules = parseValidationRules(col.validationRules);
        if (rules.length === 0) return;

        // For new rows: validate all fields with rules
        // For existing rows: validate only fields the user actually edited
        if (
          !modifiedRow.isNew &&
          !gridDataHook.modifiedCells.has(`${modifiedRow.rowId}:${fieldName}`)
        )
          return;

        const value = modifiedRow.data[fieldName];
        const label = col.caption || col.title || fieldName;
        const error = validateField(rules, value, label, col);

        if (error) {
          errors.set(`${modifiedRow.rowId}:${fieldName}`, error);
        }
      });
    });

    if (errors.size > 0) {
      gridDataHook.setValidationErrors(errors);
      return false;
    }

    setIsBatchSaving(true);
    const entityParentId = resolveGridParentId({
      parentId,
      parentSource: metadata.parentSource,
      detailFormKey,
      parentGridKey,
      tabsKey,
    });

    try {
      // 1. Delete marked rows (excluding temp rows)
      const rowsToDelete = Array.from(gridDataHook.markedForDeletion).filter(
        (rowId) => !rowId.startsWith('temp_') && rowId !== '0'
      );

      if (rowsToDelete.length > 0) {
        const deletionEntities: Entity[] = rowsToDelete.map((rowId) => {
          const entity: Entity = {
            Id: rowId,
            NodeKey: metadata.contentKey,
            Fields: [],
          };

          if (entityParentId) entity.ParentId = entityParentId;

          return entity;
        });

        const deleteResult = await deleteBatch(deletionEntities).unwrap();
        if (deleteResult.data.Code !== 1) {
          throw new Error(deleteResult.data.Error || 'Delete failed');
        }

        const deletedIds = new Set(rowsToDelete);
        gridDataHook.setDisplayData((rows) =>
          rows.filter((row) => !deletedIds.has(String(row.Id)))
        );
        gridDataHook.setMarkedForDeletion(
          (ids) => new Set([...ids].filter((id) => !deletedIds.has(id)))
        );
        gridDataHook.setModifiedRows(
          (rows) => new Map([...rows].filter(([id]) => !deletedIds.has(id)))
        );
        const isDeletedCell = (key: string) => deletedIds.has(key.split(':')[0]);
        gridDataHook.setModifiedCells(
          (cells) => new Set([...cells].filter((key) => !isDeletedCell(key)))
        );
        gridDataHook.setValidationErrors(
          (errors) => new Map([...errors].filter(([key]) => !isDeletedCell(key)))
        );
      }

      // 2. Save existing rows before new rows
      const rowsToSave: Entity[] = [];

      // The backend validates and saves rows sequentially.
      const orderedRows = Array.from(gridDataHook.modifiedRows.values()).sort(
        (a, b) => Number(a.isNew) - Number(b.isNew)
      );

      orderedRows.forEach((modifiedRow) => {
        // Rows marked for deletion must not be saved.
        if (gridDataHook.markedForDeletion.has(modifiedRow.rowId)) {
          return;
        }

        const fields: EntityField[] = [];

        // Add all modified fields
        parsedColumns.forEach((col) => {
          const fieldName = col.field || col.dataField;
          if (!fieldName) return;
          // Backend-generated new rows can contain required read-only values (for example CrmRelationId).
          // Existing rows must still not submit fields that the metadata marks as non-editable.
          if (!modifiedRow.isNew && !isColumnEditable(col)) return;
          if (modifiedRow.data[fieldName] !== undefined) {
            fields.push({
              FieldName: fieldName,
              StringValue: formatFieldValueForSave(
                modifiedRow.data[fieldName],
                col,
                normalizeMediaBeforeSave
              ),
            });
          }
        });

        // CRITICAL: Add parent relationship field for new rows (if parentId provided)
        if (modifiedRow.isNew && parentId) {
          const parentRelationField = parsedColumns.find(
            (col) =>
              col.additionalData?.relationType === 'parent' ||
              col.field?.toLowerCase().includes('parent')
          );

          if (parentRelationField) {
            const fieldName = parentRelationField.field || parentRelationField.dataField;
            if (fieldName) {
              fields.push({
                FieldName: fieldName,
                StringValue: parentId,
              });
            }
          }
        }

        const entity: Entity = {
          NodeKey: metadata.contentKey,
          Fields: fields,
        };

        if (!modifiedRow.isNew) {
          entity.Id = modifiedRow.rowId;
        }

        if (entityParentId) entity.ParentId = entityParentId;

        rowsToSave.push(entity);
      });

      if (rowsToSave.length > 0) {
        const saveResult = await saveBatch(rowsToSave).unwrap();
        if (saveResult.data.Code !== 1) {
          throw new Error(saveResult.data.Error || 'Save failed');
        }
      }

      // 3. Clear state and refetch
      gridDataHook.setDisplayData(gridData);
      gridDataHook.setModifiedRows(new Map());
      gridDataHook.setModifiedCells(new Set());
      gridDataHook.setMarkedForDeletion(new Set());
      gridDataHook.setValidationErrors(new Map());
      refetchGridData();

      // Child-row saves can update aggregate fields on the parent entity.
      // The batch mutation invalidates only the child grid.
      if (parentGridKey && parentGridKey !== metadata.contentKey) {
        dispatch(gridApi.util.invalidateTags([{ type: 'GridData', id: `node:${parentGridKey}` }]));
      }

      return true;
    } catch (error) {
      console.error('Batch save failed:', error);
      throw error; // Let caller handle error display
    } finally {
      setIsBatchSaving(false);
    }
  }, [
    metadata,
    enableBatchOperations,
    parentId,
    detailFormKey,
    parentGridKey,
    tabsKey,
    parsedColumns,
    gridDataHook,
    gridData,
    saveBatch,
    deleteBatch,
    refetchGridData,
    dispatch,
  ]);

  // Grid state persistence callbacks
  const handleSaveGridState = useCallback(
    (
      currentColumnVisibility: VisibilityState,
      currentColumnSizing: ColumnSizingState,
      currentSorting: SortingState,
      currentColumnOrder?: string[]
    ) => {
      const state: PersistedGridState = {
        columnVisibility: currentColumnVisibility,
        columnSizing: currentColumnSizing,
        pageSize,
        sorting: currentSorting.map((s) => ({ selector: s.id, desc: s.desc })),
        columnOrder:
          currentColumnOrder && currentColumnOrder.length > 0 ? currentColumnOrder : undefined,
      };
      persistState(state);
    },
    [pageSize, persistState]
  );

  const handleClearGridState = useCallback(() => {
    clearPersistedState();
    // Reset local state to defaults
    setPageSize(DEFAULT_PAGE_SIZE);
    // Bump trigger so useDataGridState resets TanStack Table's visual sorting/filters
    setFilterResetTrigger((prev) => prev + 1);
  }, [clearPersistedState]);

  // Navigation guard (if enabled)
  const navGuard = useNavigationGuard({
    hasUnsavedChanges: enableNavigationGuard ? gridDataHook.hasUnsavedChanges : false,
    onBatchSave: async () => {
      await handleBatchSave();
    },
    onCancelChanges: rowOps.handleCancelAll,
  });

  return {
    // Metadata
    metadata,
    metadataLoading,
    metadataError,
    parsedColumns,
    fieldKeys,
    inlineEditable,

    // Data
    gridData,
    displayData: gridDataHook.displayData,
    totalCount,
    serverSummary,
    lookupMapsAsMap,
    rawLookupItemsAsMap,
    filterLookupMapsAsMap,
    configurationTables,
    dropdownBaseFilter,
    dataLoading,
    dataFetching,
    dataError,
    refetchGridData,

    // Filtering (pagination, sorting, filtering)
    gridFiltering: {
      pageSize,
      pageIndex,
      handlePageSizeChange,
      handlePageChange,
      handleSortChange,
      handleFilterChange,
      resetAllFilters,
      handleDateRangeChange,
      filterResetTrigger,
      searchTerm,
      handleSearchChange,
    },

    // State
    modifiedRows: gridDataHook.modifiedRows,
    modifiedCells: gridDataHook.modifiedCells,
    markedForDeletion: gridDataHook.markedForDeletion,
    setMarkedForDeletion: gridDataHook.setMarkedForDeletion,
    hasUnsavedChanges: gridDataHook.hasUnsavedChanges,
    validationErrors: gridDataHook.validationErrors,

    // Show deleted
    showDeleted,
    handleShowDeletedToggle,
    commandCaptionOverrides: visibilityFilters.commandCaptionOverrides,
    toggleGridVisibilityFilter,

    // Handlers
    handleDataUpdate: rowOps.handleDataUpdate,
    handleRowDataUpdate: rowOps.handleRowDataUpdate,
    handleAddNewRow: rowOps.handleAddNewRow,
    handleAddNewRowWithData: rowOps.handleAddNewRowWithData,
    handleCancelAll: rowOps.handleCancelAll,
    handleRollbackRow: rowOps.handleRollbackRow,
    handleInlineRowDelete,
    handleBatchSave: enableBatchOperations ? handleBatchSave : async () => false,

    // Batch saving state
    isBatchSaving: enableBatchOperations ? isBatchSaving : false,

    // Navigation guard
    navGuard: enableNavigationGuard ? navGuard : undefined,

    // Grid state persistence
    persistedState,
    persistedStateFetching,
    persistedSortingForTable,
    initialSortingForTable,
    initialColumnFiltersForTable,
    columnFiltersSessionKey: sessionColumnFiltersKey ?? undefined,
    handleSaveGridState,
    handleClearGridState,

    // Timesheets
    dateRange,
  };
};
